import { ReactNode } from 'react';

import { clsx } from 'clsx';

import styles from './OptionCards.module.css';

export type TOption<TValue extends string> = {
  value: TValue;
  label: ReactNode;
  icon?: ReactNode;
};

type TOptionCardsProps<TValue extends string> = {
  name: string;
  options: TOption<TValue>[];
  value: TValue | undefined;
  onChange: (value: TValue) => void;
};

/**
 * A single-select list of tappable radio cards (loan duration, loan purpose).
 * Real radio inputs for keyboard/screen-reader support; selection is driven by
 * the checked state.
 *
 * Not orion's `Radio` — its icon-capable compound API isn't in our pinned
 * version. Swap once orion is upgraded.
 */
export const OptionCards = <TValue extends string>({
  name,
  options,
  value,
  onChange,
}: TOptionCardsProps<TValue>) => (
  <div className={styles.options} role="radiogroup">
    {options.map((option) => (
      <label
        key={option.value}
        className={clsx(styles.card, {
          [styles.selected]: value === option.value,
        })}
      >
        <input
          type="radio"
          className={styles.input}
          name={name}
          value={option.value}
          checked={value === option.value}
          onChange={() => onChange(option.value)}
        />
        {option.icon && (
          <span className={styles.icon} aria-hidden>
            {option.icon}
          </span>
        )}
        <span className={styles.label}>{option.label}</span>
        <span className={styles.radio} aria-hidden />
      </label>
    ))}
  </div>
);
